/**
 * BlockNodeVariables — read-only chips of the variables a strategy block COLLECTS
 * (blockContract.outputAssignment), shown under a SELECTED block node.
 *
 * Encoding (approved design — .design/block-node-assigned-variables):
 *   filled = required / outlined = optional ; color primary = global (no taskIds) /
 *   warning = task-scoped. Tooltip carries the full "Required · Task-scoped" detail.
 *
 * Data is STATIC: assignments come from the registry block (already in memory) and the
 * variable definitions from the strategy variable record threaded via NodeData — no fetch.
 *
 * LAYOUT CONTRACT (B1-W3): this row grows the block card past BLOCK_HEIGHT; the
 * canvas spacing reserves that growth via
 * `strategy-blocks/utils/blockNodeHeightEstimator.ts` (BLOCK_NODE_VARIABLES_METRICS
 * mirrors the paddings/chip metrics below — update BOTH together).
 */
import React, { useMemo } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { BlockVariableInfo } from '@modules/reactFlow/types/flow-canvas-types';

interface OutputAssignmentLike {
  variableId?: string;
  id?: string;
  priority?: string;
}

export interface BlockNodeVariablesProps {
  assignments?: OutputAssignmentLike[];
  variablesById?: Record<string, BlockVariableInfo>;
}

interface ResolvedVar {
  key: string;
  name: string;
  required: boolean;
  isGlobal: boolean;
  known: boolean;
}

const BlockNodeVariables: React.FC<BlockNodeVariablesProps> = ({ assignments = [], variablesById = {} }) => {
  const resolved = useMemo<ResolvedVar[]>(() => {
    const seen = new Set<string>();
    const out: ResolvedVar[] = [];
    for (const a of assignments) {
      const id = a?.variableId || a?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const def = variablesById[id];
      out.push({
        key: id,
        name: def?.name || id,
        required: Boolean(def?.required),
        isGlobal: !(def?.taskIds && def.taskIds.length > 0),
        known: Boolean(def),
      });
    }
    // Required first, then alphabetical — mirrors the Law of Variable Distribution priority.
    return out.sort(
      (x, y) => Number(y.required) - Number(x.required) || x.name.localeCompare(y.name),
    );
  }, [assignments, variablesById]);

  // Only render when the block actually collects variables (per user: show "if there are variables").
  if (resolved.length === 0) return null;

  return (
    <Box
      data-testid="block-node-variables"
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        borderBottomLeftRadius: '8px', // match the node card radius
        borderBottomRightRadius: '8px',
        px: 1.5,
        py: 1,
        pointerEvents: 'auto',
      }}
    >
      <Typography
        variant="caption"
        data-testid="block-node-variables-caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 0.75,
        }}
      >
        {`Collects ${resolved.length} variable${resolved.length === 1 ? '' : 's'}`}
      </Typography>

      {resolved.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          No variables collected by this block.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {resolved.map((v) =>
            v.known ? (
              <Tooltip key={v.key} arrow title={`${v.required ? 'Required' : 'Optional'} · ${v.isGlobal ? 'Global' : 'Task-scoped'}`}>
                <Chip
                  size="small"
                  label={v.name}
                  variant={v.required ? 'filled' : 'outlined'}
                  color={v.isGlobal ? 'primary' : 'warning'}
                  data-testid={`block-var-chip-${v.key}`}
                  sx={{ maxWidth: 240 }}
                />
              </Tooltip>
            ) : (
              <Tooltip key={v.key} arrow title={`"${v.key}" is not in this strategy's variables (unknown variable)`}>
                <Chip
                  size="small"
                  label={v.key}
                  variant="outlined"
                  data-testid={`block-var-chip-${v.key}`}
                  sx={{ borderStyle: 'dashed', color: 'text.disabled', fontFamily: 'monospace', maxWidth: 220 }}
                />
              </Tooltip>
            ),
          )}
        </Box>
      )}
    </Box>
  );
};

export default BlockNodeVariables;
